import { GridTableLayout, HeaderAction, actionColumn, column, emptyCell, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { ProgressPill, formatDate } from "src/components";
import { MilestoneActionCell } from "src/routes/projects/dynamic-schedules/draft-mode/DraftScheduleTable";
import { ScheduleDraftMode_PlanMilestoneFragment } from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import { createMilestoneDetailsPageUrl } from "src/RouteUrls";
import { pluralize } from "src/utils";
import { DraftScheduleLayoutState } from "../components/draftScheduleFilters";
import { ScheduleDateVariance } from "../components/ScheduleVariance";
import { ScheduleSnapshotsHookResult, useScheduleSnapshots } from "../components/useScheduleSnapshots";

type DynamicSchedulesMilestoneTableProps = {
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[];
  projectId: string;
  hasDraftChanges: boolean;
  layoutState?: DraftScheduleLayoutState;
  actions?: HeaderAction[];
};

// TODO: Move into components or draft dir or wherever we decide given the size of this refactor
export function DynamicSchedulesMilestoneTable(props: DynamicSchedulesMilestoneTableProps) {
  const { planMilestones, projectId, hasDraftChanges, layoutState, actions } = props;
  const { getPlanMilestoneSnapshot } = useScheduleSnapshots(projectId);
  const columns = useMemo(
    () => createColumns(getPlanMilestoneSnapshot, hasDraftChanges),
    [getPlanMilestoneSnapshot, hasDraftChanges],
  );
  const rows = useMemo(() => createRows(planMilestones), [planMilestones]);

  return (
    <GridTableLayout
      hideEditColumns
      layoutState={layoutState}
      actions={actions}
      emptyFallback="No milestones found"
      tableProps={{
        as: "virtual",
        columns,
        rows,
        sorting: { on: "client", initial: ["startDate", "ASC"] },
        rowStyles: { data: { rowLink: (row) => createMilestoneDetailsPageUrl(projectId, row.data.id) } },
      }}
    />
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ScheduleDraftMode_PlanMilestoneFragment };

type Row = HeaderRow | DataRow;

function createColumns(
  getPlanMilestoneSnapshot: ScheduleSnapshotsHookResult["getPlanMilestoneSnapshot"],
  hasDraftChanges: boolean,
) {
  return [
    column<Row>({ id: "name", header: "Name", data: ({ name }) => name, mw: "240px", clientSideSort: true }),
    column<Row>({
      id: "startDate",
      header: "Est. Start",
      data: ({ estStartDate }) => ({
        content: () => <>{formatDate(estStartDate, "monthShort")}</>,
        value: estStartDate?.toPT().toLegacyDate(),
      }),
      w: "120px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "endDate",
      header: "Est. End",
      data: ({ estEndDate, id }) => ({
        content: () => (
          <>
            {formatDate(estEndDate, "monthShort")}
            <ScheduleDateVariance current={estEndDate} baseline={getPlanMilestoneSnapshot(id)?.estEndDate} />
          </>
        ),
        value: formatDate(estEndDate, "monthShort"),
        sortValue: estEndDate,
      }),
      w: "140px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "progress",
      header: "Progress",
      data: ({ progress }) => ({
        content: () => <ProgressPill changeColorOnCompleted progress={progress} />,
        value: progress,
      }),
      w: "180px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "duration",
      header: "Duration",
      data: ({ durationInDays }) => ({
        content: () => (
          <span>
            {durationInDays} {pluralize(durationInDays, "day", "days")}
          </span>
        ),
        sortValue: durationInDays,
      }),
      w: "90px",
      clientSideSort: true,
    }),
    actionColumn<Row>({
      id: "actions",
      header: emptyCell,
      data: (row) => <MilestoneActionCell milestone={row} hasDraftChanges={hasDraftChanges} />,
      w: "48px",
      clientSideSort: false,
    }),
  ];
}

function createRows(data: ScheduleDraftMode_PlanMilestoneFragment[]) {
  return [simpleHeader, ...data.map((pm) => ({ id: pm.id, kind: "data" as const, data: pm }))];
}
